import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditOrganizationComposer } from "@/forms/organizations"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Building, Loader2 } from "lucide-react"
import type { Organization } from "@/types/organizations"
import { useToast } from "@/contexts/ToastContext"

function EditOrganizationPage() {
  const { organizationNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading } = useApiQuery('getOrganizationByNumber', { orgNumber: organizationNumber }, {})

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = (data as any)?.content?.organization

  const organization: Organization | null = raw
    ? {
        id: raw.orgNumber?.toString() || '',
        orgNumber: raw.orgNumber,
        orgCode: raw.orgCode,
        name: raw.name,
        type: raw.type,
        role: raw.role,
        email: raw.emailId || raw.email,
        emailId: raw.emailId,
        phone: raw.phone,
        address: raw.address,
        website: raw.website,
        country: raw.country,
        taxId: raw.taxId,
        accountManagerId: raw.accountManagerId,
        employeeCount: raw.employeeCount,
        isArchived: !!raw.archiveDate || raw.isArchived || false,
        archiveDate: raw.archiveDate,
        createdAt: raw.createdAt,
        updatedAt: raw.updatedAt,
      }
    : null

  const handleBack = () => navigate({ to: '/organizations' })

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0 overflow-hidden">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Building className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Organization</CardTitle>
              <CardDescription className="text-base">Update organization details</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          {organization ? (
            <EditOrganizationComposer
              // Re-mount (and re-seed the form) when a fresh copy arrives — the composer
              // freezes defaultValues at mount, so without a data-versioned key it would
              // keep showing the previously-cached values after an edit+refetch.
              key={`org-${organization.orgNumber}-${(raw as any)?.updatedAt ?? ''}`}
              organizationId={organization.id}
              initialValues={organization}
              submitLabel="Save Organization"
              showHeader={false}
              onSuccess={handleBack}
              onError={(error) => toast({ title: 'Error', description: `Failed to update organization: ${error.message}`, type: 'error' })}
              onCancel={handleBack}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Organization not found.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/organizations/$organizationNumber_/edit")({
  staticData: {
    title: 'Edit Organization',
    isSecured: true,
  },
  component: EditOrganizationPage,
})
